import { Button, Input, Label, SelectionChip, SuggestionInput } from "@coval/ui"

import { MAX_METADATA_FILTER_VALUES } from "@/lib/metadataFilters"

interface MetadataFilterValueInputProps {
  id: string
  isAnyOf: boolean
  value: string
  values: string[]
  onValueChange: (value: string) => void
  onValuesChange: (values: string[]) => void
  valueOptions?: string[]
  scalarMaxLength?: number
  onAddFilter: () => void
}

export function MetadataFilterValueInput({
  id,
  isAnyOf,
  value,
  values,
  onValueChange,
  onValuesChange,
  valueOptions,
  scalarMaxLength,
  onAddFilter,
}: MetadataFilterValueInputProps) {
  const atValueLimit = values.length >= MAX_METADATA_FILTER_VALUES
  const canAddValue = (next: string) => !atValueLimit && !values.includes(next)
  const addValue = (next: string) => {
    if (canAddValue(next)) {
      onValuesChange([...values, next])
      if (next === value) {
        onValueChange("")
      }
    }
  }

  return (
    <form
      className="space-y-2"
      onSubmit={(event) => {
        event.preventDefault()
        if (isAnyOf) {
          if (value.length > 0) {
            addValue(value)
          }
        } else {
          onAddFilter()
        }
      }}
    >
      <Label htmlFor={id}>{isAnyOf ? "Values" : "Value"}</Label>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          {valueOptions ? (
            <SuggestionInput
              id={id}
              aria-label="Metadata value"
              value={value}
              onValueChange={onValueChange}
              options={valueOptions}
              placeholder="Choose or type a value..."
            />
          ) : (
            <Input
              id={id}
              maxLength={isAnyOf ? undefined : scalarMaxLength}
              value={value}
              onChange={(event) => onValueChange(event.target.value)}
              placeholder="Metadata value"
            />
          )}
        </div>
        {isAnyOf && (
          <Button
            type="submit"
            variant="secondary"
            disabled={value.length === 0 || !canAddValue(value)}
          >
            Add value
          </Button>
        )}
      </div>
      {isAnyOf && (
        <>
          <Button
            type="button"
            variant="ghost"
            disabled={!canAddValue("")}
            onClick={() => addValue("")}
          >
            Add empty value
          </Button>
          <div className="flex flex-wrap gap-2">
            {values.map((item, index) => (
              <SelectionChip
                key={index}
                className="max-w-full"
                label={item === "" ? "(empty)" : item}
                removeLabel={`Remove value ${item === "" ? "(empty)" : item}`}
                onRemove={() =>
                  onValuesChange(values.filter((_, i) => i !== index))
                }
              />
            ))}
          </div>
          <p className="text-xs text-coval-text-muted">
            Matches any selected value exactly. Add up to{" "}
            {MAX_METADATA_FILTER_VALUES} values.
          </p>
        </>
      )}
    </form>
  )
}
